// First-party form, no tracking SDK, cookies, local storage, or background requests.
export const TASTING_LIST_ID = 'RNszpt';
export const TASTING_LISTS = Object.freeze({ de: TASTING_LIST_ID, en: TASTING_LIST_ID });
export const SIGNUP_BUTTON_CONSENT = Object.freeze({
  de: 'Mit deiner Anmeldung erhältst du Tasting-Newsletter von Delias Whiskyshop GmbH auf Englisch. Jederzeit abmeldbar.',
  en: 'By signing up, you agree to receive tasting newsletters in English from Delias Whiskyshop GmbH. Unsubscribe anytime.',
});
export function subscriptionPayload({ language, email, firstName = '', whatsapp = '', consent }) {
  if (!Object.hasOwn(TASTING_LISTS, language) || consent !== true) throw Error('Invalid consent or language');
  email = String(email).trim();
  firstName = String(firstName).trim();
  whatsapp = String(whatsapp).trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254 || firstName.length > 80 || whatsapp.length > 40) throw Error('Invalid fields');
  const properties = {
    delia_tasting_signup_language: language,
    delia_tasting_email_language: 'en',
    delia_tasting_consent_version: '2026-09-06-signup-button-v1',
    delia_tasting_email_consent: 'requested_2026-09-06-signup-button-v1',
    delia_tasting_consent_method: 'signup_button',
    delia_tasting_consent_text: SIGNUP_BUTTON_CONSENT[language],
  };
  // Custom contact field only. Never phone_number, SMS or WhatsApp subscription.
  if (whatsapp) properties.delia_whatsapp_number_requested = whatsapp;
  const attributes = { email, properties, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
  if (firstName) attributes.first_name = firstName;
  return { data: {
    type: 'subscription',
    attributes: { custom_source: `Delia Tasting signup ${language.toUpperCase()} · 2026-09-06`, profile: { data: { type: 'profile', attributes } } },
    relationships: { list: { data: { type: 'list', id: TASTING_LISTS[language] } } },
  } };
}

export function initializeTastingSignup(root = document, fetchImpl = fetch) {
  for (const form of root.querySelectorAll('[data-delia-tasting-signup]')) {
    if (form.dataset.initialized) continue;
    const language = form.dataset.language;
    if (!Object.hasOwn(TASTING_LISTS, language)) continue;
    form.dataset.initialized = 'true';
    const en = language === 'en';
    const fields = form.querySelector('fieldset');
    const status = form.querySelector('[role="status"]');
    // Keep native validation in the browser's language. Consent is the explicit
    // submission of this standalone newsletter form, followed by native DOI.
    let busy = false;
    let submitted = false;
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || submitted || !form.reportValidity()) return;
      if (form.elements.website.value) return;
      let payload;
      try {
        payload = subscriptionPayload({language, email: form.elements.email.value, firstName: form.elements.firstName.value, whatsapp: form.elements.whatsapp.value, consent: true});
      } catch {
        status.textContent = en ? 'Please check your entries.' : 'Bitte prüfe deine Eingaben.';
        return;
      }
      busy = true;
      fields.disabled = true;
      status.textContent = en ? 'Submitting…' : 'Wird übermittelt…';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetchImpl('https://a.klaviyo.com/client/subscriptions/?company_id=SNkQrt', {
          method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify(payload), signal: controller.signal,
        });
        if (response.status !== 202) throw Error('Subscription not accepted');
        submitted = true;
        fields.hidden = true;
        form.reset();
        status.textContent = en
          ? 'Please check your inbox and confirm your email address. Check your spam folder too. Your subscription is only complete after confirmation.'
          : 'Bitte bestätige deine E-Mail-Adresse über den Link in der englischen Bestätigungs-E-Mail. Schau auch im Spam-Ordner nach. Erst danach ist deine Anmeldung abgeschlossen.';
      } catch {
        fields.disabled = false;
        status.textContent = en
          ? 'We could not confirm your submission. Check your inbox before trying again, or contact info@deliawhisky.de.'
          : 'Die Übermittlung konnte nicht bestätigt werden. Prüfe vor einem neuen Versuch dein Postfach oder melde dich bei info@deliawhisky.de.';
      } finally {
        clearTimeout(timeout);
        busy = false;
        status.focus();
      }
    });
    fields.disabled = false;
  }
}
if (typeof document !== 'undefined') {
  initializeTastingSignup();
  document.addEventListener('shopify:section:load', event => initializeTastingSignup(event.target));
}
